/**
 * Bitrix24 API Schemas
 *
 * Pure Zod schemas for Bitrix24 integration endpoints.
 * SINGLE SOURCE OF TRUTH for API contract.
 */

import { z } from 'zod/v4';
import { fieldStatusSchema } from './settings';

// ============================================================================
// CRM Entity Types
// ============================================================================

/**
 * Bitrix24 CRM entity types for field mapping and webhook integration
 *
 * - `deal` - Sales opportunities with stages, amounts, and forecasting
 * - `contact` - Individual people (customers, leads, partners)
 * - `lead` - Unqualified opportunities before conversion to deals
 * - `company` - Organizations and businesses
 *
 * AICA analysis results can be synced to custom fields on any of these entities.
 */
export const crmEntityTypeSchema = z.enum(['deal', 'contact', 'lead', 'company']);
export const entityTypeSchema = crmEntityTypeSchema; // Alias for compatibility

// ============================================================================
// AICA Field Keys
// ============================================================================

/**
 * AICA analysis fields that can be synced to Bitrix24 CRM
 *
 * Maps AICA-generated insights to custom fields in Bitrix24:
 * - `transcript` - Full conversation text (string field)
 * - `summary` - AI-generated summary (text field)
 * - `qualityScore` - Overall quality score 0-100 (integer/double field)
 * - `saleProbability` - Conversion likelihood 0-100 (integer/double field)
 * - `keywords` - Extracted entities and topics (string field, comma-separated)
 * - `sentiment` - Emotional tone analysis (string/enumeration field)
 *
 * Field mappings are configured via POST /api/v1/bitrix24/fields/mappings
 */
export const aicaFieldKeySchema = z.enum([
  'transcript',
  'summary',
  'qualityScore',
  'saleProbability',
  'keywords',
  'sentiment',
]);

// ============================================================================
// Fields Schemas
// ============================================================================

export const createFieldsBodySchema = z.object({
  fields: z.array(aicaFieldKeySchema).min(1),
  /** Absent = every card type delivery writes to (deal, lead, contact, company). */
  entityType: crmEntityTypeSchema.optional(),
});

export const listFieldsQuerySchema = z.object({
  /** Absent = every card type; a field counts as created only if all carry it. */
  entityType: crmEntityTypeSchema.optional(),
});

/**
 * Bitrix24 field types for custom field creation and mapping
 *
 * Primitive types:
 * - `string` - Short text (up to 255 chars)
 * - `integer` - Whole numbers
 * - `double` - Decimal numbers
 * - `boolean` - Yes/No checkboxes
 * - `datetime` - Date and time
 * - `date` - Date only
 * - `enumeration` - Dropdown lists with predefined values
 * - `file` - File attachments
 *
 * CRM-specific reference types:
 * - `user` - Reference to Bitrix24 user
 * - `crm_status` - CRM status/stage reference
 * - `crm_currency` - Currency selector
 * - `crm_company` - Link to company entity
 * - `crm_contact` - Link to contact entity
 * - `crm_lead` - Link to lead entity
 * - `crm_deal` - Link to deal entity
 *
 * Returned by GET /api/v1/bitrix24/fields when listing available fields.
 */
export const fieldTypeSchema = z.enum([
  'string',
  'integer',
  'double',
  'boolean',
  'datetime',
  'date',
  'enumeration',
  'file',
  'user',
  'crm_status',
  'crm_currency',
  'crm_company',
  'crm_contact',
  'crm_lead',
  'crm_deal',
]);

export const getFieldsQuerySchema = z.object({
  entityType: entityTypeSchema,
});

export const fieldMappingSchema = z.object({
  id: z.string(),
  entityType: entityTypeSchema,
  bitrixFieldId: z.string(),
  aicaFieldId: z.string(),
  fieldType: fieldTypeSchema,
  isRequired: z.boolean().default(false),
  isActive: z.boolean().default(true),
});

export const fieldResponseSchema = z.object({
  id: z.string(),
  title: z.string(),
  type: fieldTypeSchema,
  isRequired: z.boolean(),
  isReadOnly: z.boolean(),
  isMultiple: z.boolean(),
  items: z
    .array(
      z.object({
        ID: z.string(),
        VALUE: z.string(),
      })
    )
    .optional(),
});

export const listFieldsResponseSchema = z.object({
  fields: z.array(fieldResponseSchema),
  entityType: entityTypeSchema,
});

// ----------------------------------------------------------------------------
// AICA field provisioning — the shapes of POST/GET /api/v1/bitrix24/fields
// ----------------------------------------------------------------------------

const aicaFieldRefSchema = z.object({
  /** Aelo field key, e.g. 'qualityScore' */
  name: z.string(),
  /** Bitrix24 user-field name, e.g. 'UF_CRM_AICA_QUALITY' */
  fieldId: z.string(),
});

/**
 * Response of POST /api/v1/bitrix24/fields.
 *
 * `created` / `existing` / `failed` answer per FIELD across every requested
 * card type: a field is in `created` or `existing` only when no card type
 * failed it, so `created` + `existing` is "ready everywhere". `failed` has one
 * entry per (field, card type). `results` is the same answer per card type.
 */
export const createAicaFieldsResponseSchema = z.object({
  success: z.boolean(),
  created: z.array(aicaFieldRefSchema),
  existing: z.array(aicaFieldRefSchema),
  failed: z.array(
    z.object({ name: z.string(), entityType: crmEntityTypeSchema, error: z.string() })
  ),
  /** The card types that were provisioned (all four unless the caller named one). */
  entityTypes: z.array(crmEntityTypeSchema),
  results: z.array(
    z.object({
      entityType: crmEntityTypeSchema,
      created: z.array(aicaFieldRefSchema),
      existing: z.array(aicaFieldRefSchema),
      failed: z.array(z.object({ name: z.string(), error: z.string() })),
    })
  ),
});

/**
 * Response of GET /api/v1/bitrix24/fields: per Aelo field, whether EVERY
 * checked card type carries it (`created`) and its Bitrix24 name.
 */
export const listAicaFieldsResponseSchema = z.object({
  fields: z.array(fieldStatusSchema),
  entityTypes: z.array(crmEntityTypeSchema),
  /** Present only when the caller asked for one card type. */
  entityType: crmEntityTypeSchema.optional(),
});

export const createFieldMappingRequestSchema = z
  .object({
    entityType: entityTypeSchema,
    bitrixFieldId: z.string(),
    aicaFieldId: z.string(),
    fieldType: fieldTypeSchema,
    isRequired: z.boolean().optional(),
  })
  .strict();

export const updateFieldMappingRequestSchema = z
  .object({
    aicaFieldId: z.string().optional(),
    isRequired: z.boolean().optional(),
    isActive: z.boolean().optional(),
  })
  .strict();

export const fieldMappingResponseSchema = z.object({
  mapping: fieldMappingSchema,
});

export const listFieldMappingsResponseSchema = z.object({
  mappings: z.array(fieldMappingSchema),
});

// ============================================================================
// OAuth/Install Schemas
// ============================================================================

/**
 * Install body schema - used in POST /install
 */
export const installBodySchema = z.object({
  DOMAIN: z.string().optional(),
  AUTH: z.string().optional(),
  REFRESH_TOKEN: z.string().optional(),
  MEMBER_ID: z.string().optional(),
  LANG: z.string().max(35).optional(),
  applicationToken: z.string().optional(),
  clientId: z.string().optional(),
  clientSecret: z.string().optional(),
  expiresIn: z.number().optional(),
});

/**
 * Legacy Install Schema with required fields
 */
export const installSchema = z.object({
  DOMAIN: z.string(),
  AUTH: z.string(), // access_token from OAuth 2.0
  REFRESH_TOKEN: z.string().optional(),
  MEMBER_ID: z.string().optional(),
  LANG: z.string().max(35).optional(), // Bitrix24 interface language (ua -> uk, la -> es)
  applicationToken: z.string().optional(), // Bitrix24 app token for webhook verification
  clientId: z.string().optional(), // Per-installation OAuth credentials
  clientSecret: z.string().optional(),
  expiresIn: z.number().optional(), // Token lifetime in seconds (default: 3600)
});

export const installQuerySchema = z.object({
  DOMAIN: z.string().optional(),
  memberId: z.string().optional(),
  code: z.string().optional(),
  state: z.string().optional(),
});

export const oAuthCallbackQuerySchema = z.object({
  code: z.string(),
  state: z.string().optional(),
  domain: z.string().optional(),
  memberId: z.string().optional(),
  scope: z.string().optional(),
  serverEndpoint: z.string().optional(),
});

export const uninstallBodySchema = z.object({
  DOMAIN: z.string(),
  memberId: z.string(),
  auth: z
    .object({
      accessToken: z.string(),
      expiresIn: z.number(),
      scope: z.string(),
      domain: z.string(),
      memberId: z.string(),
    })
    .optional(),
});

// ============================================================================
// Webhook Settings Schema
// ============================================================================

/**
 * Organization-level webhook processing settings
 * Stored in orgs.settingsJson (JSON string in DB)
 *
 * webhookProcessingDelay: seconds to wait before fetching activity data
 * from Bitrix24 after receiving a webhook. Bitrix24 can take up to 14s+
 * to finalize MISSED_CALL/RESULT_STREAM flags, so this delay ensures
 * accurate call classification. Range: 0–30 seconds.
 */
export const webhookSettingsSchema = z.object({
  webhookProcessingDelay: z.number().int().min(0).max(30).optional(),
});

export type WebhookSettings = z.infer<typeof webhookSettingsSchema>;

// ============================================================================
// Webhook Schemas
// ============================================================================

/**
 * Bitrix24 webhook event types for real-time CRM synchronization
 *
 * Primary event (handles both calls and chats):
 * - `ONCRMACTIVITYADD` - Fired when CRM activity created (call completed or chat closed)
 *   Routes by PROVIDER_ID: IMOPENLINES_SESSION (chats) or CALL (calls)
 *
 * Legacy telephony events (deprecated, kept for backward compatibility):
 * - `ONCALLFINISH` - Fired when phone call ends
 * - `ONSESSIONFINISH` - Only available for connector apps (imopenlines.imconnector.register)
 *
 * CRM entity events (used for context enrichment and field updates):
 * - `ONCRMLEADADD` - New lead created
 * - `ONCRMDEALUPDATE` - Deal stage or fields changed
 * - `ONCRMCONTACTADD` - New contact created
 * - `ONCRMCOMPANYADD` - New company created
 *
 * Webhooks are registered via Bitrix24 REST API during app installation.
 * Received on POST /api/v1/bitrix24/webhook
 */
export const webhookEventTypeSchema = z.enum([
  'ONCRMACTIVITYADD',
  'ONCALLFINISH',
  'ONSESSIONFINISH',
  'ONCRMLEADADD',
  'ONCRMDEALUPDATE',
  'ONCRMCONTACTADD',
  'ONCRMCOMPANYADD',
]);

/**
 * Generic webhook payload schema
 */
export const webhookPayloadSchema = z.object({
  event: webhookEventTypeSchema,
  data: z.record(z.string(), z.unknown()),
  ts: z.number().optional(),
  auth: z
    .object({
      domain: z.string(),
      memberId: z.string(),
      applicationToken: z.string().optional(),
    })
    .optional(),
});

/**
 * @deprecated Use ONCRMACTIVITYADD instead (webhookActivitySchema).
 * ONCALLFINISH webhook schema - fired when Bitrix24 phone call ends
 *
 * Kept for backward compatibility with legacy integrations.
 * CALL_TYPE values: '1'=incoming, '2'=outgoing, '3'=internal (not typically analyzed)
 */
export const webhookCallSchema = z.object({
  event: z.literal('ONCALLFINISH'),
  data: z.object({
    CALL_ID: z.string(), // Unique Bitrix24 call identifier
    CALL_TYPE: z.enum(['1', '2', '3']), // '1'=incoming, '2'=outgoing, '3'=internal
    CRM_ENTITY_TYPE: z.string().optional(), // 'CONTACT', 'LEAD', 'DEAL' - linked CRM entity
    CRM_ENTITY_ID: z.string().optional(), // ID of linked CRM entity
    RECORDING_URL: z.string().optional(), // MP3/WAV recording URL (requires authentication)
    DURATION: z.number().optional(), // Call duration in seconds
  }),
  auth: z.object({
    domain: z.string(), // Bitrix24 portal domain (e.g., "company.bitrix24.com")
    memberId: z.string().optional(), // Bitrix24 member ID
    applicationToken: z.string().optional(), // App token for webhook verification
  }),
});

/**
 * @deprecated Use ONCRMACTIVITYADD instead (webhookActivitySchema).
 * ONSESSIONFINISH webhook schema - fired when Bitrix24 Open Lines chat session closes
 * Note: ONSESSIONFINISH is only available for connector apps (imopenlines.imconnector.register).
 * Our app uses ONCRMACTIVITYADD which fires when chat creates CRM activity.
 *
 * Kept for backward compatibility with legacy integrations.
 * STATUS values include: 'CLOSE' (completed), 'WAIT_CLIENT' (paused), 'SPAM' (blocked)
 */
export const webhookChatSchema = z.object({
  event: z.literal('ONSESSIONFINISH'),
  data: z.object({
    SESSION_ID: z.string(), // Open Lines session identifier
    CHAT_ID: z.string(), // Bitrix24 chat ID (use to fetch messages)
    CLIENT_ID: z.string().optional(), // Customer user ID
    OPERATOR_ID: z.string().optional(), // Agent/operator user ID
    LINE_ID: z.string().optional(), // Open Lines channel ID (WhatsApp, Telegram, etc.)
    STATUS: z.string().optional(), // Session status: 'CLOSE', 'WAIT_CLIENT', 'SPAM', etc.
    DATE_CLOSE: z.string().optional(), // Session close timestamp (ISO 8601)
  }),
  auth: z.object({
    domain: z.string(), // Bitrix24 portal domain
    applicationToken: z.string().optional(), // App token for webhook verification
  }),
});

/**
 * ONCRMLEADADD webhook schema
 */
export const webhookLeadSchema = z.object({
  event: z.literal('ONCRMLEADADD'),
  data: z.object({
    FIELDS: z.object({
      ID: z.string(), // Lead ID
      TITLE: z.string().optional(), // Lead title
      NAME: z.string().optional(), // First name
      LAST_NAME: z.string().optional(), // Last name
      PHONE: z.array(z.object({ VALUE: z.string() })).optional(), // Phone numbers
      EMAIL: z.array(z.object({ VALUE: z.string() })).optional(), // Email addresses
      STATUS_ID: z.string().optional(), // Lead status
      SOURCE_ID: z.string().optional(), // Lead source
      DATE_CREATE: z.string().optional(), // Creation date
      DATE_MODIFY: z.string().optional(), // Modification date
    }),
  }),
  auth: z.object({
    domain: z.string(),
    applicationToken: z.string().optional(), // Bitrix24 app token for verification
  }),
});

/**
 * ONCRMDEALUPDATE webhook schema
 */
export const webhookDealSchema = z.object({
  event: z.literal('ONCRMDEALUPDATE'),
  data: z.object({
    FIELDS: z.object({
      ID: z.string(), // Deal ID
      TITLE: z.string().optional(), // Deal title
      STAGE_ID: z.string().optional(), // Deal stage
      OPPORTUNITY: z.number().optional(), // Deal amount
      CURRENCY_ID: z.string().optional(), // Currency
      CONTACT_ID: z.string().optional(), // Contact ID
      COMPANY_ID: z.string().optional(), // Company ID
      DATE_CREATE: z.string().optional(), // Creation date
      DATE_MODIFY: z.string().optional(), // Modification date
    }),
    OLD_FIELDS: z
      .object({
        STAGE_ID: z.string().optional(), // Previous stage
      })
      .optional(),
  }),
  auth: z.object({
    domain: z.string(),
    applicationToken: z.string().optional(), // Bitrix24 app token for verification
  }),
});

// ============================================================================
// Type exports (inferred from schemas)
// ============================================================================

export type CrmEntityType = z.infer<typeof crmEntityTypeSchema>;
export type AicaFieldKey = z.infer<typeof aicaFieldKeySchema>;
export type InstallBody = z.infer<typeof installBodySchema>;
export type InstallQuery = z.infer<typeof installQuerySchema>;
export type OAuthCallbackQuery = z.infer<typeof oAuthCallbackQuerySchema>;
export type UninstallBody = z.infer<typeof uninstallBodySchema>;
export type FieldType = z.infer<typeof fieldTypeSchema>;
export type EntityType = z.infer<typeof entityTypeSchema>;
export type FieldMapping = z.infer<typeof fieldMappingSchema>;
export type FieldResponse = z.infer<typeof fieldResponseSchema>;
export type CreateAicaFieldsResponse = z.infer<typeof createAicaFieldsResponseSchema>;
export type ListAicaFieldsResponse = z.infer<typeof listAicaFieldsResponseSchema>;
export type CreateFieldMappingRequest = z.infer<typeof createFieldMappingRequestSchema>;
export type UpdateFieldMappingRequest = z.infer<typeof updateFieldMappingRequestSchema>;
export type WebhookEventType = z.infer<typeof webhookEventTypeSchema>;
export type WebhookPayload = z.infer<typeof webhookPayloadSchema>;
export type WebhookCall = z.infer<typeof webhookCallSchema>;
export type WebhookChat = z.infer<typeof webhookChatSchema>;
export type WebhookLead = z.infer<typeof webhookLeadSchema>;
export type WebhookDeal = z.infer<typeof webhookDealSchema>;
